import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mic, MicOff, X, Minimize2, MessageCircle, Send, 
  Volume2, Volume, Sparkles, Bot, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { AIMessage, AICharacter } from '@/components/legal/types';
import { useNavigate } from 'react-router-dom';
import AICharacterDisplay from './AICharacterDisplay';
import { detectIntent, suggestAction } from './aiUtils';
import { useAIAssistantVoiceCommands } from './voiceCommands';

const AIAssistant: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [input, setInput] = useState<string>('');
  const [character, setCharacter] = useState<AICharacter>({
    name: 'Nova',
    avatarType: 'animated',
    personality: 'supportive',
    color: 'from-violet-500 to-purple-700',
    voice: 'nova'
  });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const speechRecognitionRef = useRef<SpeechRecognition | null>(null);
  const speechSynthesisRef = useRef<SpeechSynthesisUtterance | null>(null);
  
  const toggleMute = () => {
    if (isSpeaking) {
      window.speechSynthesis?.cancel();
      setIsSpeaking(false);
    }
  };

  const toggleSpeech = () => {
    if (isListening) {
      speechRecognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try {
        speechRecognitionRef.current?.start();
        setIsListening(true);
        toast({
          title: "Voice input activated",
          description: "I'm listening... speak clearly",
        });
      } catch (error) {
        console.error('Speech recognition error:', error);
        toast({
          title: "Error activating voice input",
          description: "Please try again or use text input",
          variant: "destructive"
        });
      }
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
  };

  const handleSendMessage = (text: string = input, isVoice: boolean = false) => {
    if (!text.trim()) return;
    
    // Add user message
    const userMessage: AIMessage = {
      id: Date.now().toString(),
      content: text,
      sender: 'user',
      timestamp: new Date(),
      isVoiceMessage: isVoice
    };
    
    addMessage(userMessage);
    setInput('');
    
    // Add a loading message
    const loadingMsgId = (Date.now() + 1).toString();
    const loadingMessage: AIMessage = {
      id: loadingMsgId,
      content: '...',
      sender: 'ai',
      timestamp: new Date(),
      isProcessing: true
    };
    
    setMessages(prev => [...prev, loadingMessage]);
    
    try {
      // Detect user intent
      detectIntent(text).then(intent => {
        // Handle navigation or action based on intent
        if (intent.action === 'navigate') {
          navigate(intent.destination);
        }
        
        // Handle scheduling intents
        if (intent.action === 'schedule') {
          navigate('/calendar-scheduling');
          
          // We'll replace the loading message with actual response
          setMessages(prev => prev.filter(m => m.id !== loadingMsgId));
          
          addMessage({
            id: Date.now().toString(),
            content: `I've opened the scheduling assistant for you. Let me help you find a good time for ${intent.activityType}.`,
            sender: 'ai',
            timestamp: new Date()
          });
          
          return;
        }
        
        // Generate AI response based on user input and intent
        suggestAction(text, intent).then(response => {
          // Replace loading message with actual response
          setMessages(prev => prev.map(msg => 
            msg.id === loadingMsgId ? {
              ...msg,
              content: response,
              isProcessing: false
            } : msg
          ));
        });
      }).catch(error => {
        console.error('Error processing message:', error);
        
        // Replace loading message with error message
        setMessages(prev => prev.map(msg => 
          msg.id === loadingMsgId ? {
            ...msg,
            content: "I'm sorry, I couldn't process that request. Could you try again or phrase it differently?",
            isProcessing: false
          } : msg
        ));
      });
    } catch (error) {
      console.error('Error processing message:', error);
      
      // Replace loading message with error message
      setMessages(prev => prev.map(msg => 
        msg.id === loadingMsgId ? {
          ...msg,
          content: "I'm sorry, I couldn't process that request. Could you try again or phrase it differently?",
          isProcessing: false
        } : msg
      ));
    }
  };
  
  useAIAssistantVoiceCommands({
    sendMessage: handleSendMessage,
    toggleChat: toggleChat,
    toggleSpeech: toggleMute
  });

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addMessage({
        id: Date.now().toString(),
        content: `Hi there! I'm ${character.name}, your AI assistant. I can help with scheduling, answer questions about your music career, or just chat. How can I help you today?`,
        sender: 'ai',
        timestamp: new Date()
      });
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognitionAPI) {
      speechRecognitionRef.current = new SpeechRecognitionAPI();
      
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.continuous = true;
        speechRecognitionRef.current.interimResults = false;
        
        speechRecognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
          const transcript = event.results[event.results.length - 1][0].transcript;
          setInput(transcript);
          
          if (transcript.trim()) {
            setInput('');
            handleSendMessage(transcript, true);
          }
        };
        
        speechRecognitionRef.current.onerror = (event: SpeechRecognitionErrorEvent) => {
          console.error('Speech recognition error', event.error);
          setIsListening(false);
        };
      }
    }
    
    speechSynthesisRef.current = new SpeechSynthesisUtterance();
    if (speechSynthesisRef.current) {
      speechSynthesisRef.current.rate = 1.0;
      speechSynthesisRef.current.pitch = 1.0;
      speechSynthesisRef.current.volume = 1.0;
      
      speechSynthesisRef.current.onstart = () => {
        setIsSpeaking(true);
      };
      
      speechSynthesisRef.current.onend = () => {
        setIsSpeaking(false);
      };
    }
    
    return () => {
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.stop();
      }
      window.speechSynthesis?.cancel();
    };
  }, []);

  const speakText = (text: string) => {
    if (speechSynthesisRef.current && !isSpeaking) {
      const cleanText = text.replace(/\*\*(.*?)\*\*/g, '$1')
                           .replace(/\[(.*?)\]\(.*?\)/g, '$1')
                           .replace(/[^\w\s.,?!]/g, '');
      
      speechSynthesisRef.current.text = cleanText;
      
      if (window.speechSynthesis) {
        const voices = window.speechSynthesis.getVoices();
        const voiceMap: {[key: string]: string} = {
          'alloy': 'Google UK English Female',
          'nova': 'Google US English',
          'echo': 'Google US English Male',
          'fable': 'Google UK English Female',
          'onyx': 'Google US English Male',
          'shimmer': 'Google US English'
        };
        
        const preferredVoiceName = voiceMap[character.voice];
        const voice = voices.find(v => v.name === preferredVoiceName) || voices[0];
        if (voice) {
          speechSynthesisRef.current.voice = voice;
        }
      }
      
      window.speechSynthesis?.speak(speechSynthesisRef.current);
    }
  };

  const addMessage = (message: AIMessage) => {
    setMessages(prev => [...prev, message]);
    
    if (message.sender === 'ai' && isOpen && !isMinimized) {
      speakText(message.content);
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setTimeout(() => {
      addMessage({
        id: Date.now().toString(),
        content: `Hi there! I'm ${character.name}, your AI assistant. I can help with scheduling, answer questions about your music career, or just chat. How can I help you today?`,
        sender: 'ai',
        timestamp: new Date()
      });
    }, 100);
  };

  return (
    <div className="fixed bottom-20 right-4 z-50 flex flex-col items-end">
      <AnimatePresence>
        {isOpen && !isMinimized && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className="mb-2 w-80 sm:w-96"
          >
            <Card className="shadow-lg overflow-hidden border border-primary/10">
              <CardHeader className="p-3 bg-gradient-to-r from-violet-600 to-purple-600 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <AICharacterDisplay 
                    character={character}
                    isSpeaking={isSpeaking}
                    size="small"
                  />
                  <div>
                    <h3 className="text-white font-medium">{character.name}</h3>
                    <p className="text-white/70 text-xs">AI Assistant</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-7 w-7 text-white hover:bg-white/10"
                    onClick={clearChat}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-7 w-7 text-white hover:bg-white/10" 
                    onClick={toggleMinimize}
                  >
                    <Minimize2 className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-7 w-7 text-white hover:bg-white/10" 
                    onClick={toggleChat}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              
              <div className="max-h-96 overflow-y-auto bg-card p-3">
                {messages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`mb-3 ${message.sender === 'user' ? 'ml-auto' : 'mr-auto'} max-w-[85%]`}
                  >
                    <div className="flex items-start gap-2">
                      {message.sender === 'ai' && (
                        <div className="mt-1 flex-shrink-0">
                          <AICharacterDisplay 
                            character={character}
                            isSpeaking={isSpeaking && messages[messages.length - 1].id === message.id}
                            size="tiny"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <div 
                          className={`p-3 rounded-2xl ${
                            message.sender === 'user' 
                              ? 'bg-primary text-primary-foreground ml-auto' 
                              : 'bg-muted'
                          } ${message.isProcessing ? 'animate-pulse' : ''}`}
                        >
                          {message.isVoiceMessage && message.sender === 'user' && (
                            <Badge variant="outline" className="mb-1 bg-white/10">
                              <Mic className="h-3 w-3 mr-1" />
                              Voice Message
                            </Badge>
                          )}
                          
                          {message.content}
                        </div>
                        <div 
                          className={`text-xs text-muted-foreground mt-1 ${
                            message.sender === 'user' ? 'text-right' : 'text-left'
                          }`}
                        >
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              
              <CardFooter className="p-3 border-t bg-card">
                <div className="flex items-center gap-2 w-full">
                  <Button 
                    size="icon" 
                    variant={isListening ? "default" : "outline"}
                    className={isListening ? "text-white animate-pulse" : ""}
                    onClick={toggleSpeech}
                  >
                    {isListening ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                  </Button>
                  
                  <Textarea 
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={isListening ? "Listening..." : "Type a message..."}
                    className="min-h-10 resize-none"
                    disabled={isListening}
                  />
                  
                  <Button 
                    size="icon" 
                    onClick={() => handleSendMessage()}
                    disabled={!input.trim() && !isListening}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                  
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={toggleMute}
                    disabled={!isSpeaking}
                    className={isSpeaking ? "animate-pulse" : ""}
                  >
                    {isSpeaking ? <Volume2 className="h-4 w-4" /> : <Volume className="h-4 w-4" />}
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </motion.div>
        )}
        
        {isMinimized && (
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            className="mb-2"
          >
            <Card className="p-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg">
              <div className="flex items-center gap-2">
                <AICharacterDisplay 
                  character={character}
                  isSpeaking={false}
                  size="tiny"
                />
                <span className="text-sm">{character.name}</span>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-6 w-6 text-white hover:bg-white/10 ml-1" 
                  onClick={toggleMinimize}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
      
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Button 
          onClick={toggleChat} 
          size="icon" 
          className={`rounded-full h-14 w-14 shadow-lg transition-all duration-300 ${
            isOpen 
              ? 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600' 
              : 'bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700'
          }`}
        >
          {isOpen ? (
            <X className="h-6 w-6 text-white" />
          ) : (
            <div className="relative">
              <MessageCircle className="h-6 w-6 text-white" />
              <Sparkles className="h-3 w-3 text-yellow-300 absolute -top-1 -right-1" />
            </div>
          )}
        </Button>
      </motion.div>
    </div>
  );
};

export default AIAssistant;

